import { logger } from '../../../utils/core/logger';
import type { FormDetectionContext } from '../types';
import { CalComProvider } from './postMessage/CalComProvider';
import { CalendlyProvider } from './postMessage/CalendlyProvider';
import { HubSpotProvider } from './postMessage/HubSpotProvider';
import { PipedriveProvider } from './postMessage/PipedriveProvider';
import { TypeformProvider } from './postMessage/TypeformProvider';
import type { PostMessageSubmission, PostMessageSubProvider } from './postMessage/types';

/**
 * All registered sub-providers, checked in order for each postMessage event.
 * To add a new provider, create a file in postMessage/ and append it here.
 */
const PROVIDERS: PostMessageSubProvider[] = [
    HubSpotProvider,
    CalComProvider,
    CalendlyProvider,
    TypeformProvider,
    PipedriveProvider,
];

/**
 * Strategy 7: PostMessage Detection
 *
 * Orchestrates sub-providers that each know how to detect form submissions
 * from a specific iframe-based form provider (HubSpot, Cal.com, Typeform).
 *
 * Detection mechanisms:
 *   - postMessage listener: catches raw iframe → parent messages
 *   - Provider-specific setup: e.g., Cal.com's CustomEvent interception
 *
 * Unlike form-based strategies, this is NOT applied per-form element.
 * It's initialized by the manager in startObserving() and listens globally.
 * The postMessage itself is the eligibility signal — no CTA/form_tracking_pages check needed.
 */
export class PostMessageStrategy {
    name = 'post_message';
    private isActive = false;
    private messageListener: ((event: MessageEvent) => void) | null = null;
    private detectedProviders = new Set<string>();
    private trackedSubmissions = new Map<string, number>();

    /**
     * Start listening for postMessage events and activate sub-providers.
     * Called by FormDetectionManager in startObserving().
     */
    checkAndActivate(context: FormDetectionContext): boolean {
        if (this.isActive) return true;

        // Set up the global postMessage listener (handles HubSpot, Typeform, Cal.com raw messages)
        this.messageListener = (event: MessageEvent) => {
            this.handleMessage(event, context);
        };
        window.addEventListener('message', this.messageListener);

        // Let providers with custom setup hooks initialize (e.g., additional listeners)
        const onSubmission = (submission: PostMessageSubmission) => {
            this.trackSubmission(submission, context);
        };
        for (const provider of PROVIDERS) {
            provider.setup?.(context, onSubmission);
        }

        this.isActive = true;
        logger.debug('📨 PostMessageStrategy: listening for iframe form provider events');
        return true;
    }

    private handleMessage(event: MessageEvent, context: FormDetectionContext): void {
        let data = event.data;

        // Some providers send JSON-serialized strings instead of objects
        if (typeof data === 'string') {
            try {
                data = JSON.parse(data);
            } catch {
                return;
            }
        }

        if (!data || typeof data !== 'object') return;

        for (const provider of PROVIDERS) {
            if (!provider.detect(data)) continue;

            // Validate origin if provider declares trusted origins.
            // A same-origin message is allowed when the provider opts in via
            // trustSameOrigin (HubSpot inline / web-interactive embeds re-dispatch
            // hsFormCallback from the parent page, so origin === the page origin).
            const sameOrigin = event.origin === window.location.origin;
            if (
                provider.trustedOrigins &&
                !(provider.trustSameOrigin && sameOrigin) &&
                !provider.trustedOrigins.test(event.origin)
            ) {
                logger.debug(`📨 PostMessageStrategy: ignoring message from untrusted origin`, {
                    provider: provider.name,
                    origin: event.origin,
                });
                continue;
            }

            // Log provider detection (once per provider)
            if (!this.detectedProviders.has(provider.name)) {
                this.detectedProviders.add(provider.name);
                logger.debug(`📨 PostMessageStrategy: detected ${provider.name} provider`, {
                    origin: event.origin,
                    url: context.currentUrl,
                });
            }

            if (!provider.isSubmission(data)) {
                // Lifecycle events (onFormReady, onFormSubmit, ...) are not
                // conversions, but logging them shows how far a provider got
                // before a redirect/unload cut the flow short.
                logger.debug(`📨 PostMessageStrategy: ${provider.name} non-submission event`, {
                    eventName: data?.eventName,
                    origin: event.origin,
                });
                continue;
            }

            const submission = provider.extractData(data, event.origin);
            this.trackSubmission(submission, context);
            return;
        }

        // No provider claimed the message. If it came from a known form-provider
        // origin, surface it in debug builds — silent swallowing here hides
        // payload-shape drift (provider schema changes) from diagnosis.
        for (const provider of PROVIDERS) {
            if (provider.trustedOrigins?.test(event.origin)) {
                let payload: string;
                try {
                    payload = JSON.stringify(data).slice(0, 300);
                } catch {
                    payload = '<unserializable>';
                }
                logger.debug('📨 PostMessageStrategy: unmatched message from provider origin', {
                    origin: event.origin,
                    data: payload,
                });
                break;
            }
        }
    }

    private trackSubmission(
        submission: PostMessageSubmission,
        context: FormDetectionContext
    ): void {
        const { provider, formId, applyFormIdWhitelist, indicators, origin } = submission;

        // Deduplication: ignore duplicate submissions within 2s window
        const now = Date.now();
        const dedupKey = `${provider}:${formId}`;

        // Prune stale entries
        for (const [key, ts] of this.trackedSubmissions) {
            if (now - ts > 2000) this.trackedSubmissions.delete(key);
        }

        if (this.trackedSubmissions.has(dedupKey)) {
            logger.debug('📨 PostMessageStrategy: skipping duplicate submission', { dedupKey });
            return;
        }
        this.trackedSubmissions.set(dedupKey, now);

        logger.debug(`📨 PostMessageStrategy: ${provider} form submission detected`, {
            formId,
            origin,
            indicators,
        });

        context.onFormSubmit({
            formId,
            applyFormIdWhitelist,
            pageUrl: context.currentUrl,
            detectionMethod: this.name,
            provider,
            status: 'success',
            indicators: [`provider_${provider}`, ...indicators],
        });
    }

    cleanup(): void {
        if (this.messageListener) {
            window.removeEventListener('message', this.messageListener);
            this.messageListener = null;
        }

        // Let each provider clean up its own hooks
        for (const provider of PROVIDERS) {
            provider.cleanup?.();
        }

        this.isActive = false;
        this.detectedProviders.clear();
        this.trackedSubmissions.clear();

        logger.debug('📨 PostMessageStrategy: cleaned up');
    }
}
